/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode, useRef } from 'react';

import { css as customCss } from '@compiled/react';

import { cssMap, cx, jsx } from '@atlaskit/css';
/* eslint-disable @repo/internal/react/require-jsdoc */
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { BrandHeading } from '../brand-heading';
import { HeroGridBackground } from '../hero/grid-background';
import { Image } from '../image';
import { PageContentContainer } from '../page-content-container';
import { RelativePosition } from '../relative-position';

const styles = cssMap({
	bannerContainer: {
		display: 'flex',
		width: '100%',
		justifyContent: 'center',
	},
	backgroundColor: {
		backgroundColor: token('elevation.surface.sunken'),
	},
	bannerWidth: {
		width: '100%',
		maxWidth: '980px',
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		gap: token('space.200'),
		paddingBlock: token('space.500'),
		'@media (min-width: 30rem)': {
			flexDirection: 'row',
			alignItems: 'center',
			paddingBlock: token('space.500'),
		},
	},
	contentContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		height: '100%',
		flex: 3,
		width: '100%',
		'@media (min-width: 30rem)': {
			minHeight: '220px',
		},
	},
	imageContainer: {
		flex: 2,
		width: '100%',
		maxWidth: '400px',
	},
	edgeToEdgeWrapper: {
		position: 'absolute',
		zIndex: -1,
		insetBlockStart: 0,
		insetBlockEnd: 0,
		insetInlineStart: '50%',
		width: '100vw',
		pointerEvents: 'none',
	},
	eyebrow: {
		font: token('font.code'),
		color: token('color.text.subtle'),
	},
});

const customEdgeToEdgeWrapperStyles = customCss({
	insetInlineEnd: 'auto',
	marginInlineStart: '-50vw',
});

/**
 * Both image variants are always in the markup and the `data-color-mode` the pre-paint script has
 * already set on `<html>` shows one, so the prerendered markup is identical for every reader.
 * Choosing the `src` in JS made the server emit the light image and a dark reader's first client
 * render the dark one, a hydration mismatch React 18 does not patch.
 *
 * The hidden variant is still fetched, one wasted request per page. That is deliberate: lazy-loading
 * would keep the hidden one from loading, but this is the hero image, and a lazy hero waits for
 * layout instead of being picked up by the browser's preload scanner.
 */
const lightImageStyles = customCss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- shows one variant by color mode without re-rendering
	'html[data-color-mode="dark"] &': {
		display: 'none',
	},
});

const darkImageStyles = customCss({
	display: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- shows one variant by color mode without re-rendering
	'html[data-color-mode="dark"] &': {
		display: 'block',
	},
});

export const Banner = ({
	title,
	eyebrow,
	description,
	image,
	hasBackgroundColor,
	hasDotGrid = false,
}: {
	title: string;
	/**
	 * Optional label rendered above the title. Styled using the `font.code` token
	 * in uppercase (the "Code" typography token, all caps).
	 */
	eyebrow?: string;
	description: string;
	image?: {
		light: string;
		dark: string;
	};
	hasBackgroundColor?: boolean;
	hasDotGrid?: boolean;
}): React.JSX.Element => {
	const containerRef = useRef<HTMLDivElement>(null);
	const imageAlt = `Banner for ${title} page`;

	const content = (
		<Box xcss={styles.bannerWidth}>
			<Box xcss={styles.contentContainer}>
				<Stack space="space.100">
					{eyebrow && (
						<Box as="div" xcss={styles.eyebrow}>
							{eyebrow.toUpperCase()}
						</Box>
					)}
					<BrandHeading as="h1" size="xxlarge">
						{title}
					</BrandHeading>
					<Text size="large">{description}</Text>
				</Stack>
			</Box>
			{image && (
				<ImageContainer>
					<div css={lightImageStyles}>
						<Image src={image.light} alt={imageAlt} />
					</div>
					<div css={darkImageStyles}>
						<Image src={image.dark} alt={imageAlt} />
					</div>
				</ImageContainer>
			)}
		</Box>
	);

	return (
		<Box xcss={cx(styles.bannerContainer, hasBackgroundColor && styles.backgroundColor)}>
			{hasDotGrid ? (
				<RelativePosition>
					<div ref={containerRef} css={[styles.edgeToEdgeWrapper, customEdgeToEdgeWrapperStyles]}>
						<HeroGridBackground containerRef={containerRef} />
					</div>
					<PageContentContainer>{content}</PageContentContainer>
				</RelativePosition>
			) : (
				<PageContentContainer>{content}</PageContentContainer>
			)}
		</Box>
	);
};

const ImageContainer = ({ children }: { children: ReactNode }) => {
	return <Box xcss={styles.imageContainer}>{children}</Box>;
};
